import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { StyledButton } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    logoMobileWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    contactUsWrapper?: InjectableCss;
    signInWrapper?: InjectableCss;
    cartWrapper?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            align-items: center;
            text-align: left;
            padding: 0px;
            margin: 0px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    logoMobileWrapper: {
        css: css`
            display: flex;
            align-items: center;
            text-align: left;
            padding: 0px;
            margin: 0px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            display: flex;
            align-items: center;
            /* text-align: right; */
            flex: 1;
            padding: 0px 0px 0px 2rem;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    contactUsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0px 0px 0px 2rem;
            > div[class^="Wrapper"] {
                display: flex;
                height: 100%;
                > div[class^="sc"] {
                    display: flex;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0px 0px 0px 1rem;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    signInWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0px 0px 0px 2rem;
            > div[class^="Wrapper"] {
                display: flex;
                height: 100%;
                > div[class^="sc"] {
                    display: flex;
                    > ${StyledButton} {
                        > ${IconWrapper} {
                            ${({ theme }) => {
                                return breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            color: #fff;
                                        `,
                                        null,
                                        null,
                                        null,
                                    ],
                                    "max",
                                );
                            }}
                        }
                    }
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0px 0px 0px 1rem;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    cartWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0px 0px 0px 2rem;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0px 0px 0px 1rem;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<PageProps> = ({ id }) => (
    <StyledWrapper {...logoLinksSearchStyles.container}>
        <Hidden below="md" {...logoLinksSearchStyles.logoWrapper}>
            <Zone contentId={id} zoneName="Logo" />
        </Hidden>
        <Hidden above="sm" {...logoLinksSearchStyles.logoMobileWrapper}>
            <Zone contentId={id} zoneName="MobileLogo" />
        </Hidden>
        <Hidden below="lg" {...logoLinksSearchStyles.searchMenuWrapper}>
            <Zone contentId={id} zoneName="Search" />
        </Hidden>
        <StyledWrapper {...logoLinksSearchStyles.contactUsWrapper}>
            <Zone contentId={id} zoneName="ContactUs" />
        </StyledWrapper>
        <StyledWrapper {...logoLinksSearchStyles.signInWrapper}>
            <Zone contentId={id} zoneName="SignIn" />
        </StyledWrapper>
        <StyledWrapper {...logoLinksSearchStyles.cartWrapper}>
            <Zone contentId={id} zoneName="Cart" />
        </StyledWrapper>
    </StyledWrapper>
);

const logoLinksSearch: WidgetModule = {
    component: LogoLinksSearch,
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
